<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import Input from '@lit-element/components/input'
import { useNamespace } from '@lit-element/hooks'
import {
  formatSeconds,
  getRangeWidth,
  isValidNumber,
  toNumber,
} from './hooks/utils'

defineOptions({ name: 'VideoTimer' })

const props = defineProps({
  time: {
    type: Number,
    default: 0,
  },
  duration: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['change'])

const ns = useNamespace('video')

const currentTime = ref(props.time)
const editing = ref(false)
const width = ref(84)
const inputTime = ref('')

const input = ref<HTMLInputElement>()
const durationEl = ref<HTMLElement>()

const formattedTime = computed(() => formatSeconds(currentTime.value))

watch(
  () => props.time,
  (value) => {
    currentTime.value = value
  }
)
watch(
  () => props.disabled,
  (value) => {
    if (value) {
      editing.value = false
    }
  }
)

onMounted(() => {
  watch(
    () => props.duration,
    () => {
      nextTick(() => {
        width.value = durationEl.value
          ? getRangeWidth(durationEl.value) * 2 + 20
          : 84
      })
    },
    { immediate: true }
  )
})

function handleClick() {
  if (!props.disabled && !editing.value) {
    editing.value = true
    inputTime.value = formattedTime.value
    nextTick(() => {
      input.value?.focus()
    })
  }
}

function finishInput(confirm: boolean) {
  editing.value = false

  if (confirm) {
    const units: any = inputTime.value.trim().split(':')

    if (units.every((unit: any) => isValidNumber(unit))) {
      currentTime.value = units
        .map(toNumber)
        .reverse()
        .slice(0, 3)
        .reduce(
          (seconds: any, unit: any, i: any) => seconds + 60 ** i * unit,
          0
        )
      currentTime.value = Math.min(currentTime.value, props.duration)
      emit('change', currentTime.value)
    }
  }
}
</script>

<template>
  <div
    :class="[
      ns.e('control'),
      ns.e('timer'),
      props.disabled && ns.em('control', 'disabled'),
    ]"
    :style="{ width: `${width}px` }"
    @click="handleClick"
  >
    <Input
      v-if="editing"
      ref="input"
      v-model:value="inputTime"
      :class="ns.e('timer-input')"
      size="small"
      transparent
      @blur="finishInput(false)"
      @enter="finishInput(true)"
    />
    <template v-else>
      <span>
        {{ formattedTime }}
      </span>
      <span :class="ns.e('timer-separator')">/</span>
      <span ref="durationEl">
        {{ formatSeconds(duration) }}
      </span>
    </template>
  </div>
</template>
